import React, { useState, useEffect } from "react";
import { Dialog, Box, Button, TextField, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import DialogContent from "@mui/material/DialogContent";
import { useDispatch, useSelector } from 'react-redux';
import { sendWhatsAppOTP, verifyWhatsAppOTP, clearError, clearWhatsAppStates } from '../../../redux/slice/registerSlice';

const VerifyWhatsAppNumModal = ({
  open,
  handleClose,
  setIsWhatsappVerified,
  waNum,
  name,
  isVerified = false,
}) => {
  const dispatch = useDispatch();
  const { isLoading, error, whatsappOtpSent, whatsappVerified } = useSelector(state => state.register);
  
  const [viewOtpCont, setViewOtpCont] = useState(false);
  const [OTP, setOTP] = useState("");
  const [OTPError, setOTPError] = useState(false);
  
  // Debug logging
  console.log('WhatsApp Modal Props:', { open, waNum, name, isVerified });
  console.log('Redux State:', { isLoading, error, whatsappOtpSent, whatsappVerified });

  // Define handleModalClose before useEffects to avoid hoisting issues
  const handleModalClose = () => {
    // Reset all states when modal is closed
    setViewOtpCont(false);
    setOTP("");
    setOTPError(false);
    // Only clear Redux WhatsApp states if user is not already verified
    dispatch(clearError());
    if (!isVerified) {
      dispatch(clearWhatsAppStates());
    }
    handleClose();
  };

  // Handle successful WhatsApp verification
  useEffect(() => {
    if (whatsappVerified) {
      handleModalClose();
      setIsWhatsappVerified(true);
    }
  }, [whatsappVerified]);

  // Show OTP container when OTP is sent successfully
  useEffect(() => {
    if (whatsappOtpSent) {
      setViewOtpCont(true);
    }
  }, [whatsappOtpSent]);

  // Clear Redux errors when modal closes
  useEffect(() => {
    if (!open && error) {
      dispatch(clearError());
    }
  }, [open, error, dispatch]);

  // If already verified, don't show the modal content - just show verified status
  if (isVerified) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 2 }}>
            <Typography variant="h6" sx={{ color: "green", fontWeight: "600", mb: 2 }}>
              ✓ WhatsApp Number Already Verified
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>
              Your WhatsApp number {waNum} is already verified and ready to use.
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#66bb6a" } }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const handleSendOTP = async () => {
    try {
      if (!name || name.trim() === '') {
        alert("Name is required for verification");
        return;
      }
      
      if (!waNum || waNum.trim() === '') {
        alert("WhatsApp number is required");
        return;
      }

      console.log('Sending WhatsApp OTP with data:', { name: name.trim(), whatsappNumber: waNum.trim() });
      
      const result = await dispatch(sendWhatsAppOTP({ 
        name: name.trim(), 
        whatsappNumber: waNum.trim() 
      })).unwrap();
      
      console.log('OTP sent successfully:', result);
      setViewOtpCont(true);
    } catch (err) {
      console.error('sendOTP error:', err);
      alert(err.message || "Failed to send OTP. Please try again.");
    }
  };

  const handleVerifyOTP = async () => {
    try {
      if (!OTP || OTP.trim() === '') {
        alert("Please enter OTP");
        return;
      }
      
      console.log('Verifying WhatsApp OTP:', OTP.trim());
      
      const result = await dispatch(verifyWhatsAppOTP({ 
        otp: OTP.trim() 
      })).unwrap();
      
      console.log('OTP verified successfully:', result);
      handleModalClose();
      setIsWhatsappVerified(true);
    } catch (err) {
      console.error('verifyOTP error:', err);
      
      if (err.message && err.message.includes("Not Matched")) {
        setOTPError(true);
        setIsWhatsappVerified(false);
      } else {
        alert(err.message || "OTP verification failed. Please try again.");
      }
    }
  };

  const handleOTPChange = (e) => {
    let val = e.target.value;
    setOTP(val);
    setOTPError(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent>
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <Typography component="h1" sx={{ fontWeight: "600" }}>
            You will get an otp to your provided whatsapp number
          </Typography>
          <Button
            color="primary"
            variant="outlined"
            type="button"
            size="small"
            onClick={handleSendOTP}
            sx={{ marginLeft: "10px" }}
          >
            {isLoading && !viewOtpCont && (
              <CircularProgress
                style={{
                  width: "22px",
                  height: "22px",
                  marginRight: "10px",
                }}
                color="inherit"
              />
            )}{" "}
            Get OTP
          </Button>
        </Box>

        {viewOtpCont && (
          <Box sx={{ marginTop: "20px" }}>
            {!OTPError ? (
              <TextField
                margin="dense"
                id="otp"
                name="otp"
                label="Enter OTP"
                type="text"
                fullWidth
                variant="outlined"
                value={OTP}
                onChange={handleOTPChange}
              />
            ) : (
              <TextField
                autoFocus
                error
                margin="dense"
                id="otp"
                name="otp"
                label="Please enter correct OTP"
                type="text"
                fullWidth
                variant="outlined"
                value={OTP}
                onChange={handleOTPChange}
              />
            )}
            
            {/* Display Redux error messages */}
            {error && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ mt: 1, mb: 1 }}
              >
                {error}
              </Typography>
            )}
            
            <Button
              onClick={handleVerifyOTP}
              sx={{
                display: "block",
                marginTop: "10px",
                marginLeft: "auto",
                backgroundColor: "#4caf50",
                color: "white",
                "&:hover": {
                  backgroundColor: "#66bb6a",
                },
              }}
            >
              {isLoading && viewOtpCont && (
                <CircularProgress
                  style={{
                    width: "22px",
                    height: "22px",
                    marginRight: "10px",
                  }}
                  color="inherit"
                />
              )}{" "}
              Verify OTP
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerifyWhatsAppNumModal;
